/**
 * ============================================================================
 * PUSH NOTIFICATION SERVICE - Fully Integrated
 * ============================================================================
 * Enterprise-grade push notification handling for Android and iOS
 * Integrated with existing PokerSweepStars services
 * 
 * Features:
 * - FCM registration and token management
 * - User preference management (per notification type)
 * - Foreground notification handling
 * - Deep linking from notification taps
 * - Quiet hours support
 * - Cross-platform (Android + iOS)
 * - Integration with messageService, socialService
 * 
 * Location: web-app/src/services/pushNotificationService.ts
 * ============================================================================
 */

import { Capacitor } from '@capacitor/core';
import type { 
  Token, 
  PushNotificationSchema, 
  ActionPerformed,
  RegistrationError 
} from '@capacitor/push-notifications';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteField, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { API_BASE } from '../utils/apiBase';

//  WEB-SAFE STUB - Prevents "not implemented on web" error

const WebStub = {
  checkPermissions: async () => ({ receive: 'denied' as const }),
  requestPermissions: async () => ({ receive: 'denied' as const }),
  register: async () => {},
  addListener: (_event: string, _cb: any) => ({ remove: () => {} }),
  removeAllListeners: async () => {},
  getDeliveredNotifications: async () => ({ notifications: [] }),
  removeAllDeliveredNotifications: async () => {},
};

// Use stub on web, real module loaded dynamically on native
let PushNotifications: any = WebStub;
let pushModuleLoaded = false;

// Load real module on native platforms
const loadPushModule = async () => {
  if (pushModuleLoaded || !Capacitor.isNativePlatform()) return;
  try {
    const module = await import('@capacitor/push-notifications');
    PushNotifications = module.PushNotifications;
    pushModuleLoaded = true;
    console.log('📱 [PUSH] Native module loaded');
  } catch (err) {
    console.warn('📱 [PUSH] Failed to load native module:', err);
  }
};

// ============================================================================
// TYPES
// ============================================================================

export interface NotificationPreferences {
  // Master toggle
  enabled: boolean;
  
  // Notification types
  announcements: boolean;      // Admin announcements
  mentions: boolean;           // @ mentions in chat/feed
  directMessages: boolean;     // DMs from other users
  tableInvites: boolean;       // Invite notifications
  gameAlerts: boolean;         // Your turn, hand results
  promotions: boolean;         // Bonus offers, promotions
  
  // Quiet hours (optional)
  quietHoursEnabled: boolean;
  quietHoursStart: string;     // "22:00" (24h format)
  quietHoursEnd: string;       // "08:00" (24h format)
}

export const DEFAULT_PREFERENCES: NotificationPreferences = {
  enabled: true,
  announcements: true,
  mentions: true,
  directMessages: true,
  tableInvites: true,
  gameAlerts: true,
  promotions: false,  // Default off for promotions
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
};

export type NotificationType = 
  | 'announcement' 
  | 'mention' 
  | 'dm' 
  | 'table_invite' 
  | 'game_alert' 
  | 'promotion';

export interface PushNotificationData {
  type: NotificationType;
  tableId?: string;
  feedItemId?: string;
  storyId?: string;
  fromUserId?: string;
  conversationId?: string;
  inviteToken?: string;
  url?: string;
  [key: string]: string | undefined;
}

export interface AnnouncementRecord {
  id: string;
  title: string;
  body: string;
  sentBy: string;
  sentByUsername?: string;
  sentAt: Timestamp;
  targetCount: number;
  successCount: number;
  failedCount: number;
  data?: Record<string, string>;
}

// ============================================================================
// PUSH NOTIFICATION SERVICE CLASS
// ============================================================================

export interface PushDiagStep { step: string; ok: boolean; detail: string; }

class PushNotificationService {
  private initialized = false;
  private fcmToken: string | null = null;
  private listeners: (() => void)[] = [];

  /**
   * On-device diagnosis + manual trigger. Runs every stage of registration
   * and reports each one's outcome in plain words, so "push doesn't work"
   * turns into "step 3 failed and here is why" - readable on the phone
   * itself, no computer attached. Built after an evening lost to a phone
   * running an old binary that could not register: the panel would have
   * said so in its first two lines.
   */
  async diagnose(): Promise<PushDiagStep[]> {
    const steps: PushDiagStep[] = [];
    const add = (step: string, ok: boolean, detail: string) => {
      steps.push({ step, ok, detail });
      console.log(`📱 [PUSH_DIAG] ${ok ? '✓' : '✗'} ${step}: ${detail}`);
    };

    // 1. Platform
    const platform = Capacitor.getPlatform();
    if (!Capacitor.isNativePlatform()) {
      add('Platform', false, `${platform} - browsers do not receive app push. Test on the installed iPhone/Android app.`);
      return steps;
    }
    add('Platform', true, platform);

    // 2. Native plugin (missing = the binary predates the push fixes)
    try {
      await loadPushModule();
      await PushNotifications.checkPermissions();
      add('Native plugin', true, 'push plugin present in this binary');
    } catch (e: any) {
      add('Native plugin', false,
        `NOT in this binary (${e?.message || e}). This app build predates the push fixes - build a new version in Appflow and reinstall.`);
      return steps;
    }

    // 3. Permission
    try {
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await PushNotifications.requestPermissions();
      }
      if (perm.receive !== 'granted') {
        add('Permission', false, `${perm.receive} - enable notifications for this app in iOS Settings, then retry.`);
        return steps;
      }
      add('Permission', true, 'granted');
    } catch (e: any) {
      add('Permission', false, e?.message || String(e));
      return steps;
    }

    // 4. Register + wait for Apple/FCM to hand us a token. A temporary
    // registrationError listener captures the REAL failure reason (missing
    // APNs key, entitlement rejection) so the panel can show it instead of
    // a generic timeout.
    try {
      let registrationError: string | null = null;
      const errListener = await PushNotifications.addListener(
        'registrationError',
        (err: any) => { registrationError = err?.error || JSON.stringify(err); });
      this.setupListeners();
      await PushNotifications.register();
      const started = Date.now();
      while (!this.fcmToken && !registrationError && Date.now() - started < 10_000) {
        await new Promise(r => setTimeout(r, 300));
      }
      try { errListener?.remove?.(); } catch { /* noop */ }
      if (registrationError) {
        add('Device token', false, `registration error: ${registrationError}`);
        return steps;
      }
      if (!this.fcmToken) {
        add('Device token', false,
          'no token within 10s and no error reported. Check the APNs Authentication Key in Firebase Console (Project settings > Cloud Messaging).');
        return steps;
      }
      add('Device token', true, `received (${this.fcmToken.substring(0, 12)}…)`);
    } catch (e: any) {
      add('Device token', false, e?.message || String(e));
      return steps;
    }

    // 5. Server registration
    try {
      await this.saveTokenToFirestore(this.fcmToken);
      add('Server registration', true, 'token registered - this device should now appear in the admin list');
    } catch (e: any) {
      add('Server registration', false, e?.message || String(e));
    }
    return steps;
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  /**
   * Initialize push notifications
   * Call this after user logs in
   */
  async initialize(): Promise<boolean> {
    // Only works on native platforms (Android/iOS)
    if (!Capacitor.isNativePlatform()) {
      console.log('📱 [PUSH] Not a native platform - skipping push setup');
      return false;
    }

    // Load native module
    await loadPushModule();

    if (this.initialized) {
      console.log('📱 [PUSH] Already initialized');
      return true;
    }

    try {
      console.log('📱 [PUSH] Initializing push notifications...');
      
      // Check current permission status
      const permStatus = await PushNotifications.checkPermissions();
      console.log('📱 [PUSH] Current permission status:', permStatus.receive);
      
      if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
        // Request permission
        console.log('📱 [PUSH] Requesting permission...');
        const newStatus = await PushNotifications.requestPermissions();
        
        if (newStatus.receive !== 'granted') {
          console.log('📱 [PUSH] Permission denied by user');
          return false;
        }
        console.log('📱 [PUSH] Permission granted!');
      } else if (permStatus.receive !== 'granted') {
        console.log('📱 [PUSH] Permission not granted:', permStatus.receive);
        return false;
      }

      // Set up listeners BEFORE registering
      this.setupListeners();

      // Register with FCM/APNs. One shot used to be the whole story: a
      // cold-start network blip or slow APNs meant no token until the
      // NEXT cold start - a player with the toggle on and no token,
      // silently missing every push. The verify-and-retry loop runs in
      // the BACKGROUND: initialize() must not hold up the login chain
      // (App.tsx awaits it before featured-table setup) for up to 30s
      // when the token is slow.
      (async () => {
        for (let attempt = 1; attempt <= 3; attempt++) {
          console.log(`📱 [PUSH] Registering with push service (attempt ${attempt})...`);
          await PushNotifications.register();
          const started = Date.now();
          while (!this.fcmToken && Date.now() - started < 10_000) {
            await new Promise(r => setTimeout(r, 400));
          }
          if (this.fcmToken) {
            console.log('📱 [PUSH] ✅ Token confirmed');
            return;
          }
          console.warn(`📱 [PUSH] No token after attempt ${attempt}`);
        }
        console.warn('📱 [PUSH] ⚠️ No token after 3 attempts - the Enable PSS Notifications button can retry on demand');
      })().catch(err => console.warn('📱 [PUSH] register/verify loop failed:', err));

      this.initialized = true;
      console.log('📱 [PUSH] ✅ Initialized (token verification continues in background)');
      return true;

    } catch (error) {
      console.error('📱 [PUSH] ❌ Initialization error:', error);
      return false;
    }
  }

  /**
   * User-facing enable: runs the full permission -> register -> token ->
   * server-save chain RIGHT NOW and reports the outcome in plain words.
   * This is the "Enable PSS Notifications" button - the recovery path for
   * anyone whose startup registration silently failed.
   */
  async enablePush(): Promise<{ ok: boolean; message: string }> {
    if (!Capacitor.isNativePlatform()) {
      return { ok: false, message: 'Push runs on the installed app. On the web, notifications are not available.' };
    }
    const steps = await this.diagnose();
    const failed = steps.find(s => !s.ok);
    if (!failed) {
      return { ok: true, message: 'Notifications are active on this device. You are all set.' };
    }
    if (failed.step === 'Permission') {
      const isAndroid = Capacitor.getPlatform() === 'android';
      return {
        ok: false,
        message: isAndroid
          ? 'Notifications are turned off for this app in Android Settings. Open Settings, find PokerSweepStars under Apps, allow Notifications, then press this button again.'
          : 'Notifications are turned off for this app in iOS Settings. Open Settings, find PokerSweepStars, tap Notifications and allow them, then press this button again.',
      };
    }
    return { ok: false, message: `${failed.step} failed: ${failed.detail}` };
  }

  /**
   * Cleanup listeners (call on logout)
   */
  cleanup(): void {
    console.log('📱 [PUSH] Cleaning up listeners...');
    PushNotifications.removeAllListeners();
    this.listeners = [];
    this.initialized = false;
    this.fcmToken = null;
  }

  // ==========================================================================
  // LISTENERS
  // ==========================================================================

  private setupListeners(): void {
    // Registration success - save token
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('📱 [PUSH] ✅ Registration successful');
      console.log('📱 [PUSH] FCM/APNs Token:', token.value.substring(0, 20) + '...');
      this.fcmToken = token.value;
      // saveTokenToFirestore throws on failure (so the diagnostics panel
      // can show WHY) - catch here so the background path stays quiet.
      await this.saveTokenToFirestore(token.value).catch(() => {});
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error: RegistrationError) => {
      console.error('📱 [PUSH] ❌ Registration error:', error.error);
    });

    // Notification received while app is in FOREGROUND
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('📱 [PUSH] 📩 Received in foreground:', notification.title);
      this.handleForegroundNotification(notification);
    });

    // User TAPPED on notification (app was in background or closed)
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('📱 [PUSH] 👆 Notification tapped:', action.notification.title);
      this.handleNotificationTap(action);
    });
  }

  // ==========================================================================
  // TOKEN MANAGEMENT
  // ==========================================================================

  /**
   * Save FCM/APNs token to Firestore for this user
   */
  private async saveTokenToFirestore(token: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      console.warn('📱 [PUSH] No user logged in - cannot save token');
      return;
    }

    try {
      const platform = Capacitor.getPlatform(); // 'android' or 'ios'

      // Saved through the SERVER, not straight to Firestore. The fcmTokens
      // security rule only exists in the undeployed rules file, so the
      // direct client write was silently DENIED in production - permission
      // granted, token issued, save rejected, registered-devices list empty
      // forever. The server writes with the admin SDK, which rules cannot
      // block.
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_BASE}/registerPushToken`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ token, platform }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `registerPushToken ${res.status}`);
      }

      console.log('📱 [PUSH] ✅ Token registered via server');
    } catch (error) {
      console.error('📱 [PUSH] ❌ Error saving token:', error);
      throw error;
    }
  }

  /**
   * Remove FCM token (call on logout)
   */
  async removeToken(): Promise<void> {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Server-side for the same reason as registration: the fcmTokens
      // rules never shipped, so direct writes are denied in production.
      const idToken = await user.getIdToken();
      await fetch(`${API_BASE}/unregisterPushToken`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ token: this.fcmToken || '' }),
      });

      this.fcmToken = null;
      console.log('📱 [PUSH] ✅ Token removed');
    } catch (error) {
      console.error('📱 [PUSH] ❌ Error removing token:', error);
    }
  }

  // ==========================================================================
  // NOTIFICATION HANDLING
  // ==========================================================================

  /**
   * Handle notification received while app is in foreground
   * Show an in-app toast/banner instead of system notification
   */
  private handleForegroundNotification(notification: PushNotificationSchema): void {
    const { title, body, data } = notification;
    
    console.log('📱 [PUSH] Foreground notification:', { title, body, type: data?.type });
    
    // Dispatch custom event for app to handle (show in-app toast)
    const event = new CustomEvent('pushNotificationReceived', {
      detail: { 
        title, 
        body, 
        data: data as PushNotificationData,
        id: notification.id,
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Handle user tapping on notification
   * Navigate to appropriate screen based on notification type
   */
  private handleNotificationTap(action: ActionPerformed): void {
    const { notification } = action;
    const data = notification.data as PushNotificationData;

    console.log('📱 [PUSH] Handling tap, data:', data);

    // Determine navigation target based on notification type
    let targetUrl: string | null = null;

    switch (data?.type) {
      case 'table_invite':
        if (data.tableId) {
          targetUrl = data.inviteToken 
            ? `/game/${data.tableId}?invite=${data.inviteToken}`
            : `/game/${data.tableId}`;
        }
        break;

      case 'mention':
        if (data.feedItemId || data.storyId) {
          targetUrl = `/feed?highlight=${data.feedItemId || data.storyId}`;
        } else {
          targetUrl = '/feed';
        }
        break;

      case 'dm':
        if (data.conversationId) {
          targetUrl = `/messages?conversation=${data.conversationId}`;
        } else if (data.fromUserId) {
          targetUrl = `/messages/${data.fromUserId}`;
        } else {
          targetUrl = '/messages';
        }
        break;

      case 'announcement':
        targetUrl = '/';  // Go to home for announcements
        break;

      case 'game_alert':
        if (data.tableId) {
          targetUrl = `/game/${data.tableId}`;
        }
        break;

      case 'promotion':
        targetUrl = data.url || '/wallet';
        break;

      case 'tournament':
        // Land ON the tournament so registering is one tap away
        targetUrl = (data as any).tournamentId
          ? `/tournaments?register=${(data as any).tournamentId}`
          : '/tournaments';
        break;

      default:
        // If explicit URL provided, use it
        if (data?.url) {
          targetUrl = data.url;
        }
    }

    // A sender-provided deep link overrides the type-based default (e.g.
    // training co-op invites route to the auto-join link, not /game/...).
    if ((data as any)?.link) {
      targetUrl = (data as any).link;
    }

    if (targetUrl) {
      console.log('📱 [PUSH] Navigating to:', targetUrl);
      // Use window.location for navigation (works with any router)
      window.location.href = targetUrl;
    }
  }

  // ==========================================================================
  // USER PREFERENCES
  // ==========================================================================

  /**
   * Get user's notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences> {
    const user = auth.currentUser;
    if (!user) return DEFAULT_PREFERENCES;

    try {
      const prefsRef = doc(db, 'users', user.uid, 'settings', 'notifications');
      const prefsDoc = await getDoc(prefsRef);
      
      if (prefsDoc.exists()) {
        return { ...DEFAULT_PREFERENCES, ...prefsDoc.data() } as NotificationPreferences;
      }
      return DEFAULT_PREFERENCES;
    } catch (error) {
      console.error('📱 [PUSH] Error getting preferences:', error);
      return DEFAULT_PREFERENCES;
    }
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(prefs: Partial<NotificationPreferences>): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) return false;

    try {
      const prefsRef = doc(db, 'users', user.uid, 'settings', 'notifications');
      await setDoc(prefsRef, {
        ...prefs,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      console.log('📱 [PUSH] ✅ Preferences updated:', Object.keys(prefs));
      return true;
    } catch (error) {
      console.error('📱 [PUSH] ❌ Error updating preferences:', error);
      return false;
    }
  }

  /**
   * Toggle all notifications on/off
   */
  async setEnabled(enabled: boolean): Promise<boolean> {
    const success = await this.updatePreferences({ enabled });
    
    if (success) {
      // Also update user document for quick filtering on backend
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          pushNotificationsEnabled: enabled,
        });
      }
    }
    
    return success;
  }

  // ==========================================================================
  // ADMIN FUNCTIONS
  // ==========================================================================

  /**
   * Send announcement to all users (admin only)
   * This calls the backend endpoint
   */
  async sendAnnouncement(
    title: string, 
    body: string, 
    data?: Record<string, string>
  ): Promise<{ success: boolean; sent?: number; failed?: number; error?: string }> {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }

      const idToken = await user.getIdToken();
      
      const response = await fetch(
        `${API_BASE}/sendAnnouncement`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ title, body, data }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to send announcement' };
      }

      return {
        success: true,
        sent: result.sent,
        failed: result.failed,
      };
    } catch (error: any) {
      console.error('📱 [PUSH] Error sending announcement:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  }

  /**
   * Get announcement history (admin only)
   */
  async getAnnouncementHistory(limitCount: number = 20): Promise<AnnouncementRecord[]> {
    try {
      const q = query(
        collection(db, 'announcements'),
        orderBy('sentAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AnnouncementRecord[];
    } catch (error) {
      console.error('📱 [PUSH] Error getting announcement history:', error);
      return [];
    }
  }

  /**
   * Get count of users with push notifications enabled
   */
  async getPushEnabledUserCount(): Promise<number> {
    try {
      const q = query(
        collection(db, 'users'),
        where('fcmToken', '!=', null)
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('📱 [PUSH] Error getting push user count:', error);
      return 0;
    }
  }

  // ==========================================================================
  // UTILITY METHODS
  // ==========================================================================

  /**
   * Check if push notifications are available and enabled
   */
  async isAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    
    try {
      const permStatus = await PushNotifications.checkPermissions();
      return permStatus.receive === 'granted';
    } catch {
      return false;
    }
  }

  /**
   * Check if notifications are supported on this platform
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get current FCM/APNs token
   */
  getToken(): string | null {
    return this.fcmToken;
  }

  /**
   * Get current platform
   */
  getPlatform(): 'android' | 'ios' | 'web' {
    return Capacitor.getPlatform() as 'android' | 'ios' | 'web';
  }

  /**
   * Request permission (can be called separately from initialize)
   */
  async requestPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;

    try {
      const result = await PushNotifications.requestPermissions();
      return result.receive === 'granted';
    } catch (error) {
      console.error('📱 [PUSH] Error requesting permission:', error);
      return false;
    }
  }

  /**
   * Check current permission status
   */
  async checkPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    if (!Capacitor.isNativePlatform()) return 'denied';

    try {
      const result = await PushNotifications.checkPermissions();
      if (result.receive === 'granted') return 'granted';
      if (result.receive === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'denied';
    }
  }

  /**
   * Get delivered notifications (Android only)
   */
  async getDeliveredNotifications(): Promise<PushNotificationSchema[]> {
    if (!Capacitor.isNativePlatform()) return [];

    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications;
    } catch {
      return [];
    }
  }

  /**
   * Remove all delivered notifications
   */
  async removeAllDeliveredNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      console.error('📱 [PUSH] Error removing notifications:', error);
    }
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;